// SystemDesignDetail.jsx (v4/v5 compatible) — Special pages + RIGHT Sponsored
import React, { useEffect } from "react";
import { withRouter, Link } from "react-router-dom";
import { bySlug } from "./systemDesignChecklist";

/* import special long-form pages here */
import InterviewGamePlanDetail from "./InterviewGamePlanDetail";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* map slug → special component (easy to extend later) */
const SPECIAL_PAGES = {
  "interview-game-plan": InterviewGamePlanDetail,
};

function SystemDesignDetail({ match, history }) {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const { slug } = match.params;

  // 1) If this slug has a dedicated long-form page, render it and bail out
  const Special = SPECIAL_PAGES[slug];
  if (Special) return <Special />;

  // 2) Otherwise fall back to generic section renderer
  const section = bySlug[slug];
  if (!section) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Unknown section. <Link to="/systemDesign">Back to System Design</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-lg py-4">
      {/* breadcrumb + back */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>{" "}
        / <span className="text-muted">{section.title}</span>
      </nav>

      {/* layout: MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-9">
          <header className="mb-3 d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h1 className="h3 mb-1">
                {section.id}) {section.title}
              </h1>
              <p className="text-muted mb-0" style={{ maxWidth: 760 }}>
                {section.summary}
              </p>
            </div>
            <div className="d-print-none">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => history.goBack()}
              >
                ← Back
              </button>
            </div>
          </header>

          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h2 className="h6 text-uppercase text-muted">Sub-topics</h2>
              <ul className="mt-2">
                {section.bullets.map((b, i) => (
                  <li key={i} className="mb-2">
                    {b}
                  </li>
                ))}
              </ul>

              {/* optional in-article ad */}
              {ADS_ENABLED && (
                <div className="my-4 text-center d-print-none">
                  <AdSlot
                    enabled={ADS_ENABLED}
                    adSlot="5201219465"
                    style={{ display: "block", minHeight: 90, textAlign: "center" }}
                  />
                </div>
              )}

              <hr />
              <div className="mt-3">
                <h3 className="h6">How to present this section in interviews</h3>
                <p className="text-muted mb-0">
                  Start with the problem framing, list key constraints, outline
                  trade-offs, and tie choices back to requirements. Use simple
                  math where helpful, call out failure modes, and summarize with
                  clear decisions.
                </p>
              </div>
            </div>
          </section>

          {/* quick prev/next by id */}
          <div className="d-flex justify-content-between mt-3">
            {byOrder(section.id - 1)}
            {byOrder(section.id + 1)}
          </div>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}

/** Helper to render prev/next buttons by numeric id */
function byOrder(id) {
  const entries = Object.values(bySlug);
  const found = entries.find((s) => s.id === id);
  if (!found) return <span />;

  const maxId = Math.max(...entries.map((e) => e.id));
  const isPrev = id < maxId;

  return (
    <Link className="btn btn-light border" to={`/systemDesign/${found.slug}`}>
      {isPrev ? "← " : ""}
      {found.id}) {found.title}
      {isPrev ? "" : " →"}
    </Link>
  );
}

export default withRouter(SystemDesignDetail);
